// src/pages/Workflows/Execucoes/components/ExecucaoLogItem.js

import React, { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { unified } from 'unified';
import remarkParse from 'remark-parse';
import remarkRehype from 'remark-rehype';
import rehypeStringify from 'rehype-stringify';
import rehypeSanitize from 'rehype-sanitize';
import { NODE_DEFINITIONS } from '../../Editor/utils/nodeDefinitions';

const ESTADO_ICONE = {
  completed:         '✅',
  failed:            '❌',
  running:           '🔄',
  pending:           '⏳',
  skipped:           '⏭',
  skipped_preflight: '⏭',
};

async function markdownToHtml(markdown) {
  const file = await unified()
    .use(remarkParse)
    .use(remarkGfm)
    .use(remarkRehype)
    .use(rehypeSanitize)
    .use(rehypeStringify)
    .process(markdown);
  return String(file);
}

export default function ExecucaoLogItem({ no, defaultExpanded = false, bloqueado = false }) {
  const [expandido, setExpandido] = useState(defaultExpanded);
  const [copiado, setCopiado]     = useState(false);

  const def        = NODE_DEFINITIONS[no.no_tipo];
  const duracaoSeg = no.duracao_seg != null ? `${no.duracao_seg}s` : '';

  // ── Classificação do tipo de output ──────────────────────────────────────

  // Texto simples / markdown: output de prompt_ia, etc.
  const isTexto = typeof no.output === 'string';

  // Modo antigo Node.js: array de ficheiros [{ id, nome, mimetype, buffer_b64 }]
  const isFicheiros = Array.isArray(no.output)
    && no.output.length > 0
    && Boolean(no.output[0]?.buffer_b64);

  // Modo novo Python: objecto único { nome_ficheiro, bytes_b64, mimetype }
  const isDocumento = !Array.isArray(no.output)
    && no.output !== null
    && typeof no.output === 'object'
    && Boolean(no.output.bytes_b64)
    && Boolean(no.output.nome_ficheiro);

  // Determinar se existe output renderizável
  const temOutput = Boolean(no.output && (
    isTexto     ? no.output.length > 0 :
    isFicheiros ? true :
    isDocumento ? true :
    typeof no.output === 'object' ? Object.keys(no.output).length > 0 : false
  ));

  // Texto para markdown/JSON — apenas quando não é download
  const textoOutput = isTexto
    ? no.output
    : (!isFicheiros && !isDocumento && no.output)
      ? JSON.stringify(no.output, null, 2)
      : '';

  const handleCopiar = async (e) => {
    e.stopPropagation();
    try {
      const htmlBody = await markdownToHtml(textoOutput);
      const html = `<!doctype html><html><body>${htmlBody}</body></html>`;
      if (navigator.clipboard?.write && window.ClipboardItem) {
        await navigator.clipboard.write([new ClipboardItem({
          'text/html':  new Blob([html],         { type: 'text/html' }),
          'text/plain': new Blob([textoOutput],  { type: 'text/plain' }),
        })]);
      } else {
        await navigator.clipboard.writeText(textoOutput);
      }
      setCopiado(true);
      setTimeout(() => setCopiado(false), 2000);
    } catch (err) {
      console.error('Erro ao copiar:', err);
    }
  };

  return (
    <div className={`exec-log-no exec-log-no--${no.estado}`}>
      <div
        className="exec-log-no-header"
        onClick={() => !bloqueado && temOutput && setExpandido(!expandido)}
        style={{ cursor: (temOutput && !bloqueado) ? 'pointer' : 'default' }}
      >
        <span className="exec-log-no-icone">{ESTADO_ICONE[no.estado] || '❓'}</span>
        <span className="exec-log-no-nome">{def?.label || no.no_tipo}</span>
        {no.erro      && <span className="exec-log-no-erro">{no.erro}</span>}
        {duracaoSeg   && <span className="exec-log-no-dur">{duracaoSeg}</span>}
        {temOutput && !bloqueado && <span className="exec-log-no-expand">{expandido ? '▲' : '▼'}</span>}
      </div>

      {expandido && no.output && (
        <div className="exec-log-no-output">

          {/* ── Modo novo Python: objecto único { nome_ficheiro, bytes_b64, mimetype } ── */}
          {isDocumento ? (
            <div className="exec-log-downloads">
              <a
                href={`data:${no.output.mimetype};base64,${no.output.bytes_b64}`}
                download={no.output.nome_ficheiro}
                className="exec-download-btn"
              >
                📄 {no.output.nome_ficheiro}
              </a>
            </div>

          ) : isFicheiros ? (
            /* ── Modo antigo Node.js: array de ficheiros ── */
            <div className="exec-log-downloads">
              {no.output.map(f => (
                <a
                  key={f.id}
                  href={`data:${f.mimetype};base64,${f.buffer_b64}`}
                  download={f.nome}
                  className="exec-download-btn"
                >
                  📄 {f.nome}
                </a>
              ))}
            </div>

          ) : (
            /* ── Texto / Markdown / JSON ── */
            <>
              <div className="exec-log-output-toolbar">
                <button className="exec-copy-btn" onClick={handleCopiar}>
                  {copiado ? '✓ Copiado' : '📋 Copiar'}
                </button>
              </div>
              {isTexto ? (
                <div className="exec-log-markdown">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>{no.output}</ReactMarkdown>
                </div>
              ) : (
                <pre>{textoOutput}</pre>
              )}
            </>
          )}

        </div>
      )}
    </div>
  );
}